/*
 * Rules for the compose window (V53): the "Before you send" checks, the AI
 * assistant's prompts and tones, reply/forward subjects and the typing
 * animation of AI text. Kept here (with tests) so the window stays simple.
 */
import moment from "moment/moment";

import { Email } from "../../../factories/emails/models/emails.models";
import { AiWriteAction } from "../../../slices/AiSlice";
import { quillHtmlToPlainText } from "../../ai/ai.utils";

export type ComposeCheckKind =
  | "attach"
  | "subject"
  | "placeholder"
  | "unfinished";

export type ComposeCheck = { kind: ComposeCheckKind; text: string };

/** [date], [Client name], [amount]…: a word in square brackets (not [1] or a link). */
const BRACKET_GAP = /\[\s*[A-Za-z][^\]\n]{0,40}\]/;

/** Shown instead of the sender when the person has no connected mailbox (QA batch 13). */
export const NO_MAILBOX_TEXT = "No mailbox connected";
export const NO_MAILBOX_TIP =
  "Connect your mailbox on the Email page to send email.";

const ATTACH_WORDS =
  /\b(attached|attachment|attachments|enclosed|i(?:'|’)?ve attached|i have attached|see attached|attaching)\b/i;

/**
 * Things worth a second look before sending. Plain rules, no AI: a mentioned
 * attachment that isn't there, an empty subject, an unfilled {{placeholder}},
 * or a message that stops mid-sentence.
 */
export const composeChecks = ({
  html,
  subject,
  attachmentCount,
}: {
  html: string;
  subject: string;
  attachmentCount: number;
}): ComposeCheck[] => {
  const text = quillHtmlToPlainText(html || "");
  const checks: ComposeCheck[] = [];
  if (!text) return checks;
  if (ATTACH_WORDS.test(text) && attachmentCount === 0) {
    checks.push({
      kind: "attach",
      text: "You mention an attachment, but nothing is attached.",
    });
  }
  if (!subject.trim()) {
    checks.push({ kind: "subject", text: "The subject line is empty." });
  }
  if (/\{\{\s*\w+\s*\}\}/.test(text) || /\{\{\s*\w+\s*\}\}/.test(subject)) {
    checks.push({
      kind: "placeholder",
      text: "A template field like {{firstName}} hasn't been filled in.",
    });
  }
  // A gap left to fill in, like [date] or [Client name] (QA batch 13: AI drafts left them).
  if (BRACKET_GAP.test(text) || BRACKET_GAP.test(subject)) {
    checks.push({
      kind: "placeholder",
      text: "A gap like [date] hasn't been filled in.",
    });
  }
  const lastLine = text.trim().split("\n").pop()?.trim() || "";
  if (lastLine.length > 25 && !/[.!?:;)"'’”…]$/.test(lastLine)) {
    checks.push({ kind: "unfinished", text: "Your message looks unfinished." });
  }
  return checks;
};

export type ComposeTone = "professional" | "friendly" | "concise";

export const TONES: { value: ComposeTone; label: string }[] = [
  { value: "professional", label: "Professional" },
  { value: "friendly", label: "Friendly" },
  { value: "concise", label: "Concise" },
];

/** The tone becomes part of the request to the AI. */
export const toneInstruction = (tone: ComposeTone): string =>
  tone === "friendly"
    ? "Tone: warm and friendly, still professional."
    : tone === "concise"
    ? "Tone: short and to the point (a few sentences)."
    : "Tone: professional and clear.";

export const buildDraftInstruction = (prompt: string, tone: ComposeTone) =>
  [prompt.trim(), toneInstruction(tone)].filter(Boolean).join("\n\n");

/** Ready-made prompts for a new email (replies use the AI's own suggestions). */
export const NEW_EMAIL_PROMPTS = [
  "Request missing documents",
  "Remind about an upcoming deadline",
  "Friendly payment reminder",
  "Thank them for sending documents",
  "Ask for a quick call this week",
];

export const REPLY_PROMPTS = [
  "Confirm we received it",
  "Answer their question",
  "Ask for what's still missing",
];

export type ImproveAction = {
  action: AiWriteAction;
  label: string;
  short: string;
  busy: string;
  done: string;
};

export const IMPROVE_ACTIONS: ImproveAction[] = [
  {
    action: "improve",
    label: "Polish writing",
    short: "Polish",
    busy: "Polishing your email…",
    done: "Polished your email",
  },
  {
    action: "fix",
    label: "Fix spelling & grammar",
    short: "Fix grammar",
    busy: "Checking spelling and grammar…",
    done: "Fixed spelling and grammar",
  },
  {
    action: "shorten",
    label: "Make it shorter",
    short: "Shorter",
    busy: "Tightening it up…",
    done: "Made it shorter",
  },
  {
    action: "formal",
    label: "Make it more formal",
    short: "More formal",
    busy: "Making it more formal…",
    done: "Made it more formal",
  },
  {
    action: "friendlier",
    label: "Make it friendlier",
    short: "Friendlier",
    busy: "Warming up the tone…",
    done: "Made it friendlier",
  },
];

/** "RE: x" once, whatever the original had ("Re:", "RE: RE:"…). */
export const replySubject = (subject?: string) =>
  `RE: ${(subject || "").replace(/^\s*((re|fw|fwd)\s*:\s*)+/i, "").trim()}`;

export const forwardSubject = (subject?: string) =>
  `FW: ${(subject || "").replace(/^\s*((re|fw|fwd)\s*:\s*)+/i, "").trim()}`;

/** "On Tue, Oct 20, 2026 at 09:42 AM" (the line above a quoted email). */
export const quoteDateLine = (email?: Pick<Email, "date"> | null) =>
  moment.unix(email?.date || 0).format("[On] ddd, MMM DD, YYYY [at] hh:mm A");

/** The forwarded message block appended to a forwarded email. */
export const forwardHeaderHtml = (email: Email) => {
  const from = email.from?.[0];
  const who = from ? `${from.name || from.email} &lt;${from.email}&gt;` : "";
  return [
    "<p>---------- Forwarded message ----------</p>",
    `<p>From: ${who}</p>`,
    `<p>Date: ${moment
      .unix(email.date || 0)
      .format("MMM D, YYYY, h:mm A")}</p>`,
    `<p>Subject: ${email.subject || ""}</p>`,
  ].join("");
};

/**
 * Lengths for the "typing" animation of AI text: about 40 steps, cut at word
 * ends so words don't appear letter by letter, always ending at the full text.
 */
export const revealSteps = (text: string, steps = 40): number[] => {
  const total = text.length;
  if (total === 0) return [0];
  const out: number[] = [];
  for (let i = 1; i <= steps; i++) {
    let at = Math.round((total * i) / steps);
    while (at < total && !/\s/.test(text[at])) at++;
    if (!out.length || at > out[out.length - 1]) out.push(at);
  }
  if (out[out.length - 1] !== total) out.push(total);
  return out;
};

/** A first name for greetings: the person's name, or the part before "@". */
export const recipientFirstName = (r?: { name?: string; email?: string }) => {
  const name = (r?.name || "").trim();
  if (name && !name.includes("@")) return name.split(/\s+/)[0];
  const local = (r?.email || "").split("@")[0].split(/[._-]/)[0];
  if (!/^[a-z]{2,}$/i.test(local)) return "";
  if (
    /^(info|admin|finance|accounts|billing|hello|contact|office|noreply|support|ap)$/i.test(
      local,
    )
  )
    return "";
  return local[0].toUpperCase() + local.slice(1).toLowerCase();
};

export const initialsOf = (value?: string) => {
  const parts = (value || "")
    .replace(/@.*/, "")
    .split(/[\s._-]+/)
    .filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
};

// ----- Sending (Roadmap 1873) -----

const MB = 1024 * 1024;

/**
 * Size limits for one email. Attachments travel as base64 (+33%) in the request.
 * nginx on dev.procharted.com allows 30 MB per request (raised from 5 MB on
 * 2026-10-22 with the owner's OK; above it nginx answers a 413 the browser can't
 * read) and ASP.NET Core's default is ~28.6 MB. Mail providers cap a whole email
 * at about 20-25 MB once encoded, so files together may be at most 18 MB and the
 * whole request 26 MB. The backend sends big emails to Nylas as multipart.
 */
export const MAX_ATTACHMENTS_BYTES = 18 * MB;
export const MAX_SEND_REQUEST_BYTES = 26 * MB;

export const formatMb = (bytes: number) =>
  `${(Math.ceil((bytes / MB) * 10) / 10).toFixed(1)} MB`;

/** Size of the file behind a base64 string (what the person picked). */
export const base64Bytes = (content?: string) => {
  const s = (content || "").replace(/\s/g, "");
  if (!s) return 0;
  const padding = s.endsWith("==") ? 2 : s.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((s.length * 3) / 4) - padding);
};

/** Problem with adding these files to the ones already attached, or null. */
export const attachmentsProblem = (
  attachedBytes: number,
  newFileBytes: number[],
): string | null => {
  const total = newFileBytes.reduce((sum, b) => sum + b, attachedBytes);
  if (total <= MAX_ATTACHMENTS_BYTES) return null;
  return `Attachments can be at most ${formatMb(
    MAX_ATTACHMENTS_BYTES,
  )} in total (this would be ${formatMb(
    total,
  )}). Send large files through the client portal or share a link instead.`;
};

/** Problem with the size of the whole email (pictures pasted into the text count too), or null. */
export const sendSizeProblem = (requestBytes: number): string | null =>
  requestBytes <= MAX_SEND_REQUEST_BYTES
    ? null
    : `This email is too large to send (${formatMb(
        requestBytes,
      )}; the limit is ${formatMb(
        MAX_SEND_REQUEST_BYTES,
      )} including attachments and pictures in the message). Remove an attachment or picture and try again.`;

/** Nylas only needs the address and name; the picker's extra client fields are left out. */
export const nylasRecipients = (
  list: { email: string; name?: string | null }[],
): { email: string; name?: string }[] =>
  list.map(({ email, name }) => (name ? { email, name } : { email }));

export type SendFailure = { kind: "mailbox" | "other"; text: string };

const MAILBOX_TEXT = "try authorizing again";

/**
 * What to tell the person when Send fails (it used to fail silently). The
 * server answers plain sentences (400/429/502); 409 = no mailbox connected.
 */
export const sendFailure = (error: unknown): SendFailure => {
  const e = (error || {}) as { status?: unknown; data?: unknown };
  const data =
    typeof e.data === "string"
      ? e.data
      : e.data && typeof e.data === "object"
      ? String(
          (e.data as { message?: unknown; title?: unknown }).message ||
            (e.data as { title?: unknown }).title ||
            "",
        )
      : "";
  if (e.status === 409 || data.toLowerCase().includes(MAILBOX_TEXT)) {
    return {
      kind: "mailbox",
      text: "The email wasn't sent: your mailbox isn't connected (or the connection has expired). Connect it on the Email page, then click Send again.",
    };
  }
  if (e.status === 403) {
    return {
      kind: "other",
      text: "The email wasn't sent: you can only send from your own connected mailbox.",
    };
  }
  if (e.status === 413) {
    return {
      kind: "other",
      text: `The email wasn't sent: it is too large. Keep attachments under ${formatMb(
        MAX_ATTACHMENTS_BYTES,
      )} in total.`,
    };
  }
  if (typeof e.status !== "number") {
    return {
      kind: "other",
      text: "The email wasn't sent: ProCharted couldn't be reached. Check your internet connection and try again.",
    };
  }
  const sentence = data.trim();
  if (
    sentence &&
    sentence.length <= 400 &&
    !/exception|\bat [\w.]+\(/i.test(sentence) &&
    !sentence.startsWith("<")
  ) {
    return {
      kind: "other",
      text: sentence.startsWith("The email wasn't sent")
        ? sentence
        : `The email wasn't sent: ${sentence}`,
    };
  }
  return {
    kind: "other",
    text: "The email wasn't sent because of a problem on our side. Please try again in a minute.",
  };
};

// ----- Reply / Reply all recipients (QA batch 19, MAIL-023) -----

type Person = { email: string; name?: string };

const addressKey = (address?: string | null) =>
  (address || "").trim().toLowerCase();

/** People in order, each address once (ignoring case), none of `skip`. */
const uniquePeople = <T extends Person>(list: T[], skip: Set<string>): T[] => {
  const seen = new Set(skip);
  return list.filter((p) => {
    const key = addressKey(p?.email);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Who a reply goes to. Before: Reply always went to the email's From, so
 * replying to an email I SENT went to myself, and Reply all kept my own
 * address. Now (like Outlook / Gmail):
 * - an email from someone else: Reply = the sender (or its Reply-To);
 *   Reply all = the sender + the other To people, Cc = the Cc people;
 * - an email I sent: Reply = the people it went to; Reply all = the To
 *   people, Cc = the Cc people;
 * - my own address(es) are never in To / Cc (unless the email went only to
 *   me, then Reply goes back to me).
 */
export const replyRecipients = <T extends Person>(
  email: { from?: T[]; to?: T[]; cc?: T[]; reply_to?: T[] },
  replyAll: boolean,
  myAddresses: (string | null | undefined)[],
): { to: T[]; cc: T[] } => {
  const me = new Set(myAddresses.map(addressKey).filter(Boolean));
  const from = email.from || [];
  const to = email.to || [];
  const cc = email.cc || [];
  const fromMe = from.some((p) => me.has(addressKey(p?.email)));
  const replyTo = (email.reply_to || []).filter((p) => addressKey(p?.email));

  if (fromMe) {
    const others = uniquePeople(to, me);
    const ccOthers = replyAll ? uniquePeople(cc, me) : [];
    if (others.length || ccOthers.length) {
      if (!others.length) return { to: ccOthers, cc: [] };
      return {
        to: others,
        cc: ccOthers.filter(
          (c) =>
            !others.some((o) => addressKey(o.email) === addressKey(c.email)),
        ),
      };
    }
    // An email I sent only to myself: answer myself.
    return { to: uniquePeople(to.length ? to : from, new Set()), cc: [] };
  }

  const sender = replyTo.length ? replyTo : from;
  if (!replyAll) return { to: uniquePeople(sender, new Set()), cc: [] };
  const toList = uniquePeople([...sender, ...to], me);
  const taken = new Set([...me, ...toList.map((p) => addressKey(p.email))]);
  return { to: toList, cc: uniquePeople(cc, taken) };
};
